import { ChatTransportError, ChatWidget } from '@admin-platform/ui/chat-widget';
import type { ChatTransport } from '@admin-platform/ui/chat-widget';

const API_URL = import.meta.env.VITE_SUPPORT_CHAT_API_URL ?? '';

const SUGGESTIONS = [
  'What is the difference between a bundle and a module?',
  'How does usage-based pricing work in Revvly?',
  'How do I connect Claude to Revvly with MCP?',
  'How do I import my existing invoices?',
];

/**
 * Anonymous docs support chat (public corpus only — the backend policy
 * cannot reach tenant data, see apps/panel-api/src/rag/). The same
 * ChatWidget is reused inside the dashboard with an authenticated agent
 * transport.
 */
const transport: ChatTransport = {
  async send(messages) {
    let response: Response;
    // Abort a hung backend so the widget doesn't sit in a loading state forever.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20_000);
    try {
      response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
        signal: controller.signal,
      });
    } catch {
      throw new ChatTransportError(
        'We could not reach the assistant. Check your connection and try again.',
        true,
      );
    } finally {
      clearTimeout(timeoutId);
    }

    if (response.status === 429) {
      throw new ChatTransportError(
        'You are sending messages quickly — give it a moment and try again.',
        true,
      );
    }
    if (!response.ok) {
      throw new ChatTransportError(
        'The assistant is unavailable right now. Please try again later, or reach us via the contact page.',
        false,
      );
    }

    let body: { reply: string; citations?: Array<{ title: string; url: string }> };
    try {
      body = (await response.json()) as {
        reply: string;
        citations?: Array<{ title: string; url: string }>;
      };
    } catch {
      // A malformed/empty 2xx body would otherwise reject unhandled — funnel it
      // through the same friendly transport error as other failure paths.
      throw new ChatTransportError(
        'The assistant is unavailable right now. Please try again later, or reach us via the contact page.',
        false,
      );
    }
    return { reply: body.reply, citations: body.citations ?? [] };
  },
};

export function SupportChat() {
  // Not configured (e.g. local dev without the API running) — render nothing
  // rather than a dead launcher. Same pattern as the early-access form.
  if (!API_URL.trim()) {
    return null;
  }
  return (
    <ChatWidget
      transport={transport}
      title="Ask Revvly"
      subtitle="Answers from the documentation"
      suggestions={SUGGESTIONS}
      inputPlaceholder="Ask about Revvly"
    />
  );
}
